import { defineResource } from '@tnn/core'
import { z } from 'zod'
import { DILIGENCE_STEPS, diligenceKinds } from './diligence'

/**
 * People (Thananan HR): the company's employees — everyone who works here, whether or not
 * they sign in — kept apart from sign-in accounts (admin.user). An employee has at most one
 * account; drivers (tms.driver) link to their employee. Each position grants default roles,
 * so joining needs no one to pick permissions by hand. Private details (ID card, address,
 * documents) are a separate record (`hr.profile`) only HR reads.
 */

export const departmentStatuses = ['active', 'inactive'] as const

export const department = defineResource({
  app: 'hr',
  name: 'department',
  path: 'departments',
  schema: z.object({
    code: z.string().trim().toUpperCase().min(1).max(20),
    name: z.string().trim().min(1).max(100),
    status: z.enum(departmentStatuses).default('active'),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    code: { search: true, list: { width: 110, priority: 1 }, form: { span: 4 }, import: { aliases: ['รหัส', 'รหัสแผนก'] } },
    name: { search: true, list: { priority: 1 }, form: { span: 8 }, import: { aliases: ['ชื่อแผนก'] } },
    status: { filter: true, tones: { active: 'success', inactive: 'neutral' }, list: { width: 120, priority: 1 }, form: { span: 4 } },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  title: 'name',
  defaultSort: 'code',
  importKey: 'code',
  card: { title: 'name', subtitle: 'code', status: 'status' },
})

export const position = defineResource({
  app: 'hr',
  name: 'position',
  path: 'positions',
  schema: z.object({
    name: z.string().trim().min(1).max(100),
    departmentId: z.string().nullish(),
    /** Roles a new account in this position gets (an approver may only give roles they may grant) */
    roleIds: z.array(z.string()).default([]),
    status: z.enum(departmentStatuses).default('active'),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    name: { search: true, list: { priority: 1 }, form: { span: 6 }, import: { aliases: ['ตำแหน่ง', 'ชื่อตำแหน่ง'] } },
    departmentId: {
      type: 'ref',
      ref: { resource: 'hr.department', where: { status: 'active' } },
      filter: true,
      list: { priority: 1 },
      form: { span: 6 },
    },
    // Picked from the roles one may grant (not a list of every role: reading roles is the Admin app's)
    roleIds: { type: 'multiselect', ref: { resource: 'admin.role' }, list: { priority: 2 }, form: { span: 12, custom: true } },
    status: { filter: true, tones: { active: 'success', inactive: 'neutral' }, list: { width: 120, priority: 1 }, form: { span: 4 } },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  title: 'name',
  columns: ['name', 'departmentId', 'roleIds', 'status'],
  defaultSort: 'name',
  // Importing the file again updates its positions rather than adding them twice
  importKey: 'name',
  card: { title: 'name', subtitle: 'departmentId', status: 'status' },
})

/** `permanent`: monthly staff; `daily`: paid by the day; `contract`: for a fixed term */
export const employmentTypes = ['permanent', 'daily', 'contract'] as const
/** `probation` → `active`; `resigned` (left of their own accord) or `terminated`, from the day after `lastDay` */
export const employeeStatuses = ['probation', 'active', 'resigned', 'terminated'] as const

/** A person's title as written before their name (นาย, นาง, นางสาว) */
export const nameTitles = ['mr', 'mrs', 'miss'] as const

/** Days a probation lasts by default: under 120, after which the labour law's severance applies */
export const PROBATION_DAYS = 119

/**
 * hr.employee: who someone is at work — code (EMP-0001 when not given), position, branch,
 * manager, how they are employed, when they started and left. Kept by HR (all) and branch
 * leads (their branch); the employee sees theirs in Thananan OS and changes only their phone
 * there — nobody edits their own record here (a super admin excepted). `offboard`: the last
 * day and reason; from the day after, their account is closed, and cash still in their hands
 * is flagged to HR when it is recorded.
 */
export const employee = defineResource({
  app: 'hr',
  name: 'employee',
  path: 'employees',
  schema: z.object({
    /** A photo of their face (an uploaded image): on their record and as their avatar in every app */
    photoFileId: z.string().nullish(),
    /** Empty: the next EMP-0001 */
    code: z.string().trim().toUpperCase().max(20).nullish(),
    name: z.string().trim().min(1).max(120),
    nickname: z.string().trim().max(40).nullish(),
    phone: z.string().trim().max(30).nullish(),
    branchId: z.string().nullish(),
    departmentId: z.string().nullish(),
    positionId: z.string().nullish(),
    /** Their manager (หัวหน้างาน): approvals go here first */
    managerId: z.string().nullish(),
    employmentType: z.enum(employmentTypes).default('permanent'),
    /** Their own hours, when not their position's or branch's (hr.schedule) */
    scheduleId: z.string().nullish(),
    /** The diligence allowance's rates (เบี้ยขยัน, `diligenceRates`) by where they live; empty: none */
    diligence: z.enum(diligenceKinds).nullish(),
    /**
     * The allowance's step they had in a month before the system (set on ตารางเบี้ยขยัน, both or neither): it is
     * counted on from the month after, and months before it are not counted
     */
    diligenceCarriedMonth: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
      .nullish(),
    diligenceCarriedStep: z.number().int().min(1).max(DILIGENCE_STEPS).nullish(),
    startDate: z.iso.date().nullish(),
    /** Default: startDate + PROBATION_DAYS */
    probationEndDate: z.iso.date().nullish(),
    status: z.enum(employeeStatuses).default('probation'),
    /** The last day they work; their account closes the day after */
    lastDay: z.iso.date().nullish(),
    leaveReason: z.string().trim().max(300).nullish(),
    /**
     * Let go without severance (section 119: serious misconduct; or a fixed-term project job of
     * two years or less): the last payslip leaves it out
     */
    noSeverance: z.boolean().default(false),
    /** Their driver record, for drivers (licence, jobs) */
    driverId: z.string().nullish(),
    notes: z.string().max(1000).nullish(),
  }),
  fields: {
    photoFileId: { type: 'file', list: false, form: { span: 12, custom: true }, import: false, export: false },
    code: { search: true, list: { width: 110, priority: 1 }, form: { span: 3, custom: true }, import: { aliases: ['รหัสพนักงาน', 'รหัส'] } },
    name: { search: true, list: { priority: 1 }, form: { span: 6 }, import: { aliases: ['ชื่อ', 'ชื่อ-นามสกุล'] } },
    nickname: { search: true, list: { width: 110, priority: 2 }, form: { span: 3 }, import: { aliases: ['ชื่อเล่น'] } },
    phone: { type: 'phone', search: true, list: { priority: 2 }, form: { span: 4 }, import: { aliases: ['เบอร์โทร', 'โทรศัพท์'] } },
    branchId: {
      type: 'ref',
      ref: { resource: 'company.branch', where: { status: 'active' } },
      filter: true,
      list: { priority: 1 },
      form: { span: 4 },
    },
    departmentId: {
      type: 'ref',
      ref: { resource: 'hr.department', where: { status: 'active' } },
      filter: true,
      list: { priority: 2 },
      form: { span: 4 },
    },
    positionId: {
      type: 'ref',
      ref: { resource: 'hr.position', where: { status: 'active' } },
      filter: true,
      list: { priority: 1 },
      form: { span: 4 },
    },
    managerId: { type: 'ref', ref: { resource: 'hr.employee' }, list: { priority: 3, hidden: true }, form: { span: 4 } },
    employmentType: { filter: true, list: { priority: 3, hidden: true }, form: { span: 4, section: 'employment' } },
    scheduleId: {
      type: 'ref',
      ref: { resource: 'hr.schedule', where: { status: 'active' } },
      list: false,
      form: { span: 4, section: 'employment' },
    },
    diligence: { filter: true, list: { width: 130, priority: 3, hidden: true }, form: { span: 4, section: 'employment', custom: true } },
    diligenceCarriedMonth: { list: false, form: false, import: false },
    diligenceCarriedStep: { type: 'integer', list: false, form: false, import: false },
    startDate: { filter: true, list: { width: 130, priority: 2 }, form: { span: 4, section: 'employment' } },
    probationEndDate: { filter: true, list: { priority: 3, hidden: true }, form: { span: 4, section: 'employment', custom: true } },
    status: {
      filter: true,
      tones: { probation: 'info', active: 'success', resigned: 'neutral', terminated: 'danger' },
      list: { width: 130, priority: 1 },
      form: { span: 4, section: 'employment', custom: true },
    },
    lastDay: { filter: true, list: { priority: 3, hidden: true }, form: { span: 4, section: 'employment', custom: true } },
    leaveReason: { list: false, form: { span: 4, section: 'employment', custom: true } },
    noSeverance: { list: false, form: { span: 4, section: 'employment', custom: true }, import: false },
    /**
     * The account linked to them (server-managed: joining links it, or HR opens one only to hold cash, `cashOnly`, for
     * someone who never signs in; joining later carries on with it)
     */
    userId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    /** Their account cannot sign in: it only holds cash (computed) */
    cashOnly: { readOnly: true, type: 'boolean', list: false, import: false, export: false },
    driverId: { type: 'ref', ref: { resource: 'tms.driver' }, list: false, form: { span: 6, section: 'other' } },
    notes: { type: 'textarea', list: false, form: { span: 12, section: 'other' } },
  },
  title: 'name',
  columns: ['code', 'name', 'nickname', 'positionId', 'branchId', 'status', 'phone', 'departmentId', 'startDate', 'managerId', 'userId'],
  sections: ['main', 'employment', 'other'],
  defaultSort: 'code',
  importKey: 'code',
  // `offboard`: the last day and reason (the account closes after). Their link to join is an access.invite with `employeeId`
  actions: ['read', 'create', 'update', 'delete', 'import', 'export', 'print', 'offboard'],
  ownerField: 'userId',
  branchFields: ['branchId'],
  card: { title: 'name', subtitle: 'positionId', meta: ['code', 'branchId'], status: 'status', photo: 'photoFileId' },
})

/**
 * hr.profile: an employee's private details — ID card, birth date, address, emergency contact,
 * documents. One per employee; HR reads and writes it, the employee reads their own and may
 * change their address and emergency contact (logged). Branch leads do not see it.
 */
export const profile = defineResource({
  app: 'hr',
  name: 'profile',
  path: 'profiles',
  schema: z.object({
    employeeId: z.string().min(1),
    /** As on the ID card: social security and tax filings ask for it */
    title: z.enum(nameTitles).nullish(),
    idCard: z
      .string()
      .regex(/^\d{13}$/)
      .nullish(),
    birthDate: z.iso.date().nullish(),
    address: z.string().max(500).nullish(),
    emergencyName: z.string().trim().max(120).nullish(),
    emergencyPhone: z.string().trim().max(30).nullish(),
    emergencyRelation: z.string().trim().max(60).nullish(),
    /** Copies of the ID card, the contract, a driving licence… (file ids) */
    documents: z.array(z.string()).default([]),
    notes: z.string().max(1000).nullish(),
  }),
  fields: {
    employeeId: { type: 'ref', ref: { resource: 'hr.employee' }, filter: true, list: { priority: 1 }, form: { span: 8 } },
    title: { list: { width: 100, priority: 3 }, form: { span: 4 } },
    idCard: { type: 'taxId', list: { priority: 2 }, form: { span: 6 } },
    birthDate: { list: { priority: 3 }, form: { span: 6 } },
    address: { type: 'textarea', list: false, form: { span: 12 } },
    emergencyName: { list: { priority: 3 }, form: { span: 4, section: 'emergency' } },
    emergencyPhone: { type: 'phone', list: false, form: { span: 4, section: 'emergency' } },
    emergencyRelation: { list: false, form: { span: 4, section: 'emergency' } },
    documents: { type: 'json', list: false, form: { span: 12, section: 'documents', custom: true }, import: false, export: false },
    notes: { type: 'textarea', list: false, form: { span: 12, section: 'documents' } },
  },
  title: 'employeeId',
  sections: ['main', 'emergency', 'documents'],
  defaultSort: 'employeeId',
  // One per employee: importing again updates it
  importKey: 'employeeId',
  card: { title: 'employeeId', meta: ['idCard'] },
})

/** The default end of probation: the start + PROBATION_DAYS (YYYY-MM-DD) */
export function probationEnd(startDate: string): string {
  const d = new Date(`${startDate}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + PROBATION_DAYS)
  return d.toISOString().slice(0, 10)
}
